import type { RefObject } from 'react';
import { createContext, useContext, useState } from 'react';

type DropdownMenuContextType = {
  isOpen: boolean;
  radioGroupValues: RadioGroupValues;
  setRadioGroupValue: (groupId: string, value: string) => void;
  getRadioGroupValue: (groupId: string, defaultValue?: string) => string | undefined;
  hasOpenModal: boolean;
  openModal: () => void;
  closeModal: () => void;
  closeDropdownMenu: () => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
};

const DropdownMenuContext = createContext<DropdownMenuContextType>({
  isOpen: false,
  radioGroupValues: {},
  setRadioGroupValue: () => undefined,
  getRadioGroupValue: () => undefined,
  hasOpenModal: false,
  openModal: () => undefined,
  closeModal: () => undefined,
  closeDropdownMenu: () => undefined,
  triggerRef: { current: null },
});

export type DropdownMenuProviderProps = {
  children: React.ReactNode;
  value: DropdownMenuContextType;
};

type RadioGroupValues = Record<string, string>;

export const DropdownMenuProvider: React.FC<DropdownMenuProviderProps> = ({ children, value: providedValue }) => {
  const [radioGroupValues, setRadioGroupValues] = useState<RadioGroupValues>({});

  const setRadioGroupValue = (groupId: string, value: string) => {
    setRadioGroupValues((prev) => ({
      ...prev,
      [groupId]: value,
    }));
  };

  const getRadioGroupValue = (groupId: string, defaultValue?: string) => {
    return radioGroupValues[groupId] !== undefined ? radioGroupValues[groupId] : defaultValue;
  };

  const value = {
    ...providedValue,
    radioGroupValues,
    setRadioGroupValue,
    getRadioGroupValue,
  };

  return <DropdownMenuContext.Provider value={value}>{children}</DropdownMenuContext.Provider>;
};

export const useDropdownMenu = () => {
  const context = useContext(DropdownMenuContext);
  if (!context) {
    throw new Error('useDropdownMenu must be used within a DropdownMenu component');
  }
  return context;
};
